import * as THREE from 'three';
import { Ktx2Error, VK_FORMAT_ASTC_6x6_SRGB_BLOCK, readKtx2Cube } from '@vrlearning/pano-maths';

/**
 * A 360° photo's cube as the media worker made it (ADR-041, format C): a KTX2
 * file of raw ASTC 6x6 sRGB blocks, six 2048 px faces, one level, no
 * supercompression. Nothing is decoded: the header is checked, the faces are
 * views into the fetched bytes, and they go up to the GPU as they are. No
 * WebAssembly, no worker, no transcoder -- and so no change to the CSP.
 */

/** The cube's faces, ready to upload: views into the file's buffer, held until then. */
export type AstcCube = {
  /** A face's side, px. */
  readonly size: number;
  /** +X, −X, +Y, −Y, +Z, −Z: WebGL's order. */
  readonly faces: readonly Uint8Array[];
  /** The six faces' bytes: what the cube takes on the GPU. */
  readonly bytes: number;
};

/** The six faces of a raw ASTC 6x6 sRGB cube; throws (a `Ktx2Error`) for anything else. */
export function astcCube(file: ArrayBuffer): AstcCube {
  const cube = readKtx2Cube(new Uint8Array(file));
  if (cube.vkFormat !== VK_FORMAT_ASTC_6x6_SRGB_BLOCK) {
    throw new Ktx2Error(`vkFormat ${cube.vkFormat}, expected ASTC 6x6 sRGB (166)`);
  }
  return {
    size: cube.width,
    faces: cube.faces,
    bytes: cube.faces.reduce((sum, face) => sum + face.byteLength, 0),
  };
}

/**
 * The background's cube from the blocks, as the JPEG path's (pano.ts,
 * cubeTexture): sRGB (three uploads it as COMPRESSED_SRGB8_ALPHA8_ASTC_6x6),
 * linear, no mipmaps -- a photo at infinity is magnified, never minified --,
 * anisotropy 1, not flipped (the faces are in WebGL's order, first row first).
 */
export function compressedCubeTexture(cube: AstcCube): THREE.CompressedCubeTexture {
  const faces = cube.faces.map((data) => ({
    width: cube.size,
    height: cube.size,
    mipmaps: [{ data, width: cube.size, height: cube.size }],
  }));
  // Each face as three reads a compressed cube's: its size and its one level.
  const texture = new THREE.CompressedCubeTexture(faces, THREE.RGBA_ASTC_6x6_Format);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 1;
  texture.flipY = false;
  texture.needsUpdate = true;
  return texture;
}
